import { z } from 'zod';
import { LocaleSchema, ShortIdSchema, TimestampSchema, UidSchema } from './primitives.js';

export const VehicleSchema = z.object({
  id: z.string(),
  plate: z
    .string()
    .regex(/^[A-Z0-9 -]+$/i)
    .max(15),
  plateLowercase: z.string().describe('Indexed lowercase for plate lookup'),
  make: z.string().optional(),
  model: z.string().optional(),
  colour: z.string().optional(),
  vehicleTypeId: z.string(),
  createdAt: TimestampSchema,
});
export type Vehicle = z.infer<typeof VehicleSchema>;

export const QuietHoursSchema = z.object({
  from: z
    .string()
    .regex(/^\d{2}:\d{2}$/)
    .default('22:00'),
  to: z
    .string()
    .regex(/^\d{2}:\d{2}$/)
    .default('08:00'),
});

/**
 * Per-user notification consent.
 *
 * CONSENT DECISION (2026-10-15): marketing push is OPT-IN. A user with no
 * stored prefs gets no marketing PUSH — the default here and the server's
 * `prefsFromUserDoc` (apps/functions marketing/dispatcher.ts) both read absence
 * as "not consented". Marketing still reaches the passive in-app inbox unless
 * the user explicitly turned marketing off (`marketing.push === false`), since
 * an inbox entry interrupts nobody. Transactional messages ("your car is
 * ready") stay default ON: they are the service the customer asked for.
 */
export const NotificationPrefsSchema = z.object({
  transactional: z.object({
    push: z.boolean().default(true),
    whatsapp: z.boolean().default(true),
    sms: z.boolean().default(true),
  }),
  marketing: z.object({
    push: z.boolean().default(false),
    whatsapp: z.boolean().default(false),
    sms: z.boolean().default(false),
  }),
  quietHours: QuietHoursSchema,
});
export type NotificationPrefs = z.infer<typeof NotificationPrefsSchema>;

export const UserProfileSchema = z.object({
  uid: UidSchema,
  name: z.string().optional(),
  phone: z.string().optional(),
  email: z.string().email().optional(),
  dob: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  birthMonth: z
    .number()
    .int()
    .min(1)
    .max(12)
    .optional()
    .describe('Birth month 1–12 (opt-in, month only — no day/year, privacy-minimal)'),
  locale: LocaleSchema.default('en'),
  mainOutletId: z.string().optional().describe('First outlet the customer scanned'),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema,
});
export type UserProfile = z.infer<typeof UserProfileSchema>;

export const LoyaltyAccountSchema = z.object({
  tenantId: z.string(),
  pointsBalance: z.number().int().nonnegative().default(0),
  lifetimePoints: z.number().int().nonnegative().default(0),
  lifetimeSpend: z.number().int().nonnegative().default(0).describe('In sen'),
  tierId: z.string().optional(),
  streakWeeks: z.number().int().nonnegative().default(0),
  punchProgress: z
    .number()
    .int()
    .nonnegative()
    .default(0)
    .describe('Qualifying paid washes accumulated toward the next free-wash reward'),
  punchCardsEarned: z
    .number()
    .int()
    .nonnegative()
    .default(0)
    .describe('Lifetime free-wash rewards earned via the punch card'),
  spinsAvailable: z
    .number()
    .int()
    .nonnegative()
    .default(0)
    .describe(
      'Banked, unspent Spin Wheel spins (1 accrues per paid wash when the wheel is on, capped at spinWheel.maxBankedSpins; spent by the spinTheWheel callable). Defaulted → backward-compatible.',
    ),
  lastVisitAt: TimestampSchema.optional(),
  // ── Customer referrals (customer-to-customer "refer a friend") ──────────
  // All optional/defaulted so the field additions are backward-compatible:
  // pre-referral accounts parse unchanged and pick up the zero defaults.
  referralCode: ShortIdSchema.optional().describe(
    "This customer's own shareable referral code at this tenant (minted by getMyReferralCode; the reverse map lives at tenants/{tid}/referralCodes/{code})",
  ),
  referredBy: z
    .object({
      referrerUid: UidSchema.describe('The customer whose code this customer used'),
      code: ShortIdSchema.describe('The code that was claimed'),
      claimedAt: TimestampSchema.describe(
        'When the attribution was recorded (before first paid wash)',
      ),
    })
    .optional()
    .describe('Attribution set by claimReferral when this NEW customer used a friend’s code'),
  referralRewardedAt: TimestampSchema.optional().describe(
    'When the dual referral reward was granted on this customer’s first paid wash (idempotency stamp — set once, never re-awarded)',
  ),
  referredCount: z
    .number()
    .int()
    .nonnegative()
    .default(0)
    .describe(
      'How many referred friends have converted (had their first paid wash) via this customer’s code',
    ),
  referralRewardsEarned: z
    .number()
    .int()
    .nonnegative()
    .default(0)
    .describe(
      'How many of those conversions actually granted this referrer a reward (referrerReward.kind !== none)',
    ),
  // ── Birthday bonus ──────────────────────────────────────────────────────
  // Optional (backward-compatible): pre-birthday accounts parse unchanged.
  birthdayBonusYear: z
    .number()
    .int()
    .optional()
    .describe(
      'Calendar year (tenant-tz) the birthday bonus was last granted; prevents re-issue within the same year (set by onTicketCollected on a paid wash during the customer’s birth month)',
    ),
  updatedAt: TimestampSchema,
});
export type LoyaltyAccount = z.infer<typeof LoyaltyAccountSchema>;

/**
 * Tenant-scoped denormalised customer index — one doc per customer who has
 * paid at this tenant at least once. Lives at
 * `tenants/{tid}/customers/{customerUid}`.
 *
 * Why this exists: firestore.rules gates `users/{uid}/...` per-uid (the
 * customer's own data is private), so an operator cannot list "all my
 * customers" by querying `users/...`. The customer index doc is the
 * tenant-side projection an operator can query, sort, and search by plate
 * or phone.
 *
 * Maintained by `onTicketCollected` (Cloud Function, admin SDK) — never
 * written by client code. Customers themselves do not see this doc.
 *
 * Search-indexed fields:
 *   - `platesLower[]`     — array-contains match for exact-plate lookup
 *   - `phoneNormalised`   — digits-only string for prefix-range lookup
 *                           (Malaysia: stored without country prefix +/0)
 *
 * Visible-list fields (no privacy-sensitive content here — only what the
 * operator already saw on the wash receipt or via their own ticket history):
 *   - `displayName`       — best-effort name (profile.name OR last-ticket name OR "Customer")
 *   - `tierId`            — current tier id (denormalised from loyaltyAccount)
 *   - `lifetimeSpendSen`  — denormalised running total
 *   - `lifetimePoints`    — denormalised running total
 *   - `lastVisitAt`       — sortable for "recent customers" default view
 *   - `firstVisitAt`      — set on first index write; never updated
 *   - `visits`            — incrementing visit counter
 *   - `streakWeeks`       — denormalised consecutive-week streak (drives the
 *                           streak-loss retention nudge; canonical on the account)
 */
export const TenantCustomerIndexSchema = z.object({
  id: UidSchema.describe('Same as customerUid; doc id = uid for direct lookup'),
  customerUid: UidSchema,
  tenantId: z.string().min(1),
  displayName: z.string().min(1).max(120),
  phoneNormalised: z
    .string()
    .describe('Digits only; empty string when no phone. Used for prefix-range search.'),
  platesLower: z
    .array(z.string())
    .default([])
    .describe('Lowercased plates from users/{uid}/vehicles for array-contains search'),
  /**
   * Denormalised operator-applied tag ids (Sprint 16). The canonical doc is
   * `users/{uid}/customerTagsByTenant/{tid}.tags`; setCustomerTags writes both
   * locations atomically so /customers can render chips without per-row reads.
   * onTicketCollected preserves whatever's already here.
   */
  tags: z.array(z.string()).default([]),
  tierId: z.string().optional(),
  lifetimeSpendSen: z.number().int().nonnegative().default(0),
  lifetimePoints: z.number().int().nonnegative().default(0),
  visits: z.number().int().nonnegative().default(0),
  /**
   * Denormalised copy of `LoyaltyAccount.streakWeeks` (consecutive tenant-tz
   * weeks with a paid wash). The canonical value lives on the per-user loyalty
   * account (`users/{uid}/loyaltyAccounts/{tid}`), which firestore.rules gates
   * per-uid so it is NOT queryable tenant-wide; this projection lets the
   * server-side streak-loss nudge (`streakRiskDaily`) find at-risk customers via
   * a bounded `lastVisitAt` range query instead of a per-customer scan.
   * `onTicketCollected` writes it in the SAME tx as `visits`/`lastVisitAt`.
   * Optional + defaulted → backward-compatible (pre-streak index docs read 0).
   */
  streakWeeks: z.number().int().nonnegative().default(0),
  firstVisitAt: TimestampSchema,
  lastVisitAt: TimestampSchema,
  updatedAt: TimestampSchema,
});
export type TenantCustomerIndex = z.infer<typeof TenantCustomerIndexSchema>;
